import React from 'react';
import { Phone, Envelope, User, Plus, X, MapPin } from '@phosphor-icons/react';

/**
 * Producer Contact editor — name + email + multiple phone numbers.
 * Used on ShowCreate and ShowEdit. Riders see this info on ShowDetail so
 * they can reach out with questions, refund requests, or chargeback
 * prevention before escalating to their bank.
 *
 * Props:
 *  - value: { producer_contact_name, producer_contact_email, producer_contact_phones[] }
 *  - onChange: (patch) => void — merge-patches parent state
 *  - testIdPrefix: string prefix for data-testid attributes
 */
export const ProducerContactCard = ({ value, onChange, testIdPrefix = 'producer-contact' }) => {
  const phones = value?.producer_contact_phones || [];

  const updatePhone = (idx, newValue) => {
    const next = [...phones];
    next[idx] = newValue;
    onChange({ producer_contact_phones: next });
  };
  const addPhone = () => onChange({ producer_contact_phones: [...phones, ''] });
  const removePhone = (idx) => onChange({ producer_contact_phones: phones.filter((_, i) => i !== idx) });

  return (
    <div
      className="bg-[#161616] border border-[#262626] rounded-lg p-6"
      data-testid={`${testIdPrefix}-card`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-full bg-[#0096FF]/15 flex items-center justify-center flex-shrink-0">
          <User size={18} weight="duotone" className="text-[#0096FF]" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
            Producer Contact
          </h2>
          <p className="text-xs text-[#9E9E9E] mt-0.5 max-w-lg">
            Shown publicly on the show page so riders can reach you directly. Reduces chargebacks
            and support escalations — riders who can call you almost never dispute with their bank.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-[#F8F8F8] mb-1.5">
            Contact Name
          </label>
          <input
            type="text"
            value={value?.producer_contact_name || ''}
            onChange={(e) => onChange({ producer_contact_name: e.target.value })}
            placeholder="e.g. Jane Smith"
            className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF] transition-colors"
            data-testid={`${testIdPrefix}-name`}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#F8F8F8] mb-1.5">
            <span className="inline-flex items-center gap-1.5">
              <Envelope size={13} weight="bold" /> Contact Email
            </span>
          </label>
          <input
            type="email"
            value={value?.producer_contact_email || ''}
            onChange={(e) => onChange({ producer_contact_email: e.target.value })}
            placeholder="producer@example.com"
            className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF] transition-colors"
            data-testid={`${testIdPrefix}-email`}
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-xs font-medium text-[#F8F8F8] mb-1.5">
          <span className="inline-flex items-center gap-1.5">
            <Phone size={13} weight="bold" /> Phone Number{phones.length > 1 ? 's' : ''}
          </span>
        </label>
        {phones.length === 0 && (
          <p className="text-[11px] text-[#6B6B6B] italic mb-2" data-testid={`${testIdPrefix}-no-phones`}>
            No phone numbers added — riders will only see the email.
          </p>
        )}
        <div className="space-y-2">
          {phones.map((phone, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="tel"
                value={phone}
                onChange={(e) => updatePhone(idx, e.target.value)}
                placeholder={idx === 0 ? 'Primary (e.g. 555-123-4567)' : `Alt #${idx + 1}`}
                className="flex-1 px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF] transition-colors"
                data-testid={`${testIdPrefix}-phone-${idx}`}
              />
              <button
                type="button"
                onClick={() => removePhone(idx)}
                className="p-2 text-[#9E9E9E] hover:text-[#FF3B30] transition-colors"
                title="Remove phone"
                data-testid={`${testIdPrefix}-phone-remove-${idx}`}
              >
                <X size={14} weight="bold" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addPhone}
          className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] hover:text-white hover:border-[#0096FF]/50 text-xs font-semibold rounded-md transition-colors"
          data-testid={`${testIdPrefix}-phone-add`}
        >
          <Plus size={12} weight="bold" />
          Add {phones.length === 0 ? 'Phone Number' : 'Another Phone'}
        </button>
      </div>

      {/* Check payable + mailing address — both shown to riders who pay
          by mailed check. Producers often have a different legal entity
          name than the contact (e.g., contact is the secretary, but the
          check is made out to the org or the bank account holder). */}
      <div className="mt-4 pt-4 border-t border-[#262626]">
        <label className="block text-xs font-medium text-[#F8F8F8] mb-1.5">
          <span className="inline-flex items-center gap-1.5">
            <User size={13} weight="bold" /> Make Checks Payable To
          </span>
        </label>
        <input
          type="text"
          value={value?.check_payable_to || ''}
          onChange={(e) => onChange({ check_payable_to: e.target.value })}
          placeholder="e.g. NBHA of Indiana, or Kimberly Rickly Or Jimmy Bryant"
          className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF] transition-colors"
          data-testid={`${testIdPrefix}-check-payable-to`}
        />
        <p className="text-[11px] text-[#6B6B6B] mt-1">
          Whose name riders should write on the check. Defaults to your contact name if blank.
        </p>

        <label className="block text-xs font-medium text-[#F8F8F8] mt-4 mb-1.5">
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={13} weight="bold" /> Check Mailing Address
          </span>
        </label>
        <textarea
          rows={3}
          value={value?.check_mailing_address || ''}
          onChange={(e) => onChange({ check_mailing_address: e.target.value })}
          placeholder={'Where riders should mail checks. e.g.\nNBHA of Indiana\nP.O. Box 8130\nNorth Vernon, IN 47265'}
          className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF] transition-colors resize-none"
          data-testid={`${testIdPrefix}-check-address`}
        />
        <p className="text-[11px] text-[#6B6B6B] mt-1">
          Shown to riders after they choose &quot;Pay by Mail-in Check&quot;. Leave blank if you don&apos;t accept mailed checks.
        </p>
      </div>
    </div>
  );
};
